// =====================================================================
// Chamados — a MEMÓRIA do guia GPS-TI dentro do portal.
//
// ---------------------------------------------------------------------
// O PROBLEMA
// ---------------------------------------------------------------------
// O guia roda em `<iframe sandbox>` SEM `allow-same-origin`, ou seja, em ORIGEM
// OPACA. Nessa condição o `localStorage` do iframe não é só vazio: **acessá-lo
// lança exceção**. O guia usa `localStorage` em 4 pontos, todos com `try/catch`,
// então ele não quebra — apenas esquece tudo, a cada visita:
//
//   • `gpsti-aviso`  — que a pessoa já leu a tela de abertura ("Entendido").
//                      ⚠️ É o sintoma que a Julia relatou: o aviso reaparece
//                      TODA vez que se abre o manual. O próprio guia prevê o
//                      caso ("sem memória disponível, aparece sempre").
//   • a SIGLA DA LOJA — que o guia pede uma vez e usa para personalizar. Sem
//                      memória, ele volta a perguntar a cada visita.
//
// ---------------------------------------------------------------------
// A SOLUÇÃO: o PORTAL lembra pelo guia
// ---------------------------------------------------------------------
// O portal tem `localStorage` de verdade (é a nossa origem). Então:
//   1. antes de montar o iframe, injetamos no HTML um **shim** de
//      `localStorage`, semeado com o que o portal já guardou;
//   2. o shim atende o guia em memória e, a cada escrita, avisa o portal por
//      `postMessage`;
//   3. o portal grava na SUA `localStorage`, e na visita seguinte a semente já
//      vem preenchida.
//
// ⚠️⚠️ POR QUE NO PORTAL E NÃO NO GERADOR. Fazer isso por transformação exigiria
//    reescrever os 4 pontos do guia — 4 âncoras que envelhecem a cada revisão do
//    Sebastião. O shim é **transparente**: o guia continua chamando
//    `localStorage` como sempre, sem saber que existe alguém atendendo.
//
// ⚠️⚠️ O SHIM SÓ SE INSTALA SE O `localStorage` REAL FALHAR. Rodando fora do
//    sandbox (o Sebastião abre o arquivo direto), o storage nativo funciona e o
//    shim não faz nada. Falha aberta: se a redefinição não for possível no
//    navegador, o guia volta ao comportamento de hoje — esquece, mas funciona.
//
// ---------------------------------------------------------------------
// ⚠️⚠️ A TRAVA DE SEGURANÇA — leia antes de mexer
// ---------------------------------------------------------------------
// A semente é a ÚNICA coisa que atravessa do portal para dentro do guia, e o
// guia é 800 KB de script não auditado. Por isso ela leva **só** as chaves do
// prefixo `gpsti:` — NUNCA o `localStorage` inteiro do portal, onde mora o
// `authToken`. Mandar tudo aqui entregaria a sessão ao guia por um caminho que a
// ausência de `allow-same-origin` fecha na porta da frente.
//
// E na volta o portal aceita gravar apenas chave conhecida, com tamanho limitado
// — senão o guia poderia encher o `localStorage` do portal com o que quisesse.
// =====================================================================

// A tag que fecha o bloco injetado. ⚠️ Montada, e não escrita literalmente:
// escapada (`<\/script>`) o ESLint reclama de escape inútil; literal, qualquer
// ferramenta que leia este arquivo como HTML acharia que o script acaba aqui.
const FECHA_SCRIPT = `</${'script'}>`;

/** Prefixo de tudo que o guia guarda dentro do portal. */
const PREFIXO = 'gpsti:';

/**
 * As chaves que o guia legitimamente usa.
 *
 * ⚠️ Lista FECHADA de propósito: o guia é script não auditado, e aceitar chave
 *    arbitrária transformaria o `localStorage` do portal em depósito dele.
 * ⚠️ A sigla da loja é lida do `LOJA_CHAVE` do guia — se ele renomear a chave
 *    numa revisão futura, o efeito é a loja voltar a ser perguntada toda visita
 *    (o aviso continua funcionando). Degrada, não quebra.
 */
const CHAVES = ['gpsti-aviso', 'gpsti-loja'];

/** Teto por valor. Sigla de loja e carimbo de turno são curtos; isto é folga. */
const MAX_VALOR = 200;

/** A chave é uma das que o guia pode guardar? */
export function chaveValida(k) {
  return typeof k === 'string' && CHAVES.includes(k);
}

/**
 * O que o portal já guardou para o guia, pronto para virar semente.
 *
 * ⚠️ Lê SÓ as chaves da lista, com o prefixo — nunca varre o `localStorage`.
 */
export function lerMemoriaDoGuia(storage) {
  const out = {};
  CHAVES.forEach((k) => {
    try {
      const v = storage.getItem(PREFIXO + k);
      if (typeof v === 'string') out[k] = v;
    } catch (e) { /* storage indisponível: semente vazia, e tudo bem */ }
  });
  return out;
}

/**
 * Grava, no portal, algo que o guia pediu para lembrar.
 *
 * @returns {boolean} false quando recusado (chave desconhecida ou valor grande)
 */
export function gravarMemoriaDoGuia(storage, chave, valor) {
  if (!chaveValida(chave)) return false;
  if (typeof valor !== 'string' || valor.length > MAX_VALOR) return false;
  try {
    storage.setItem(PREFIXO + chave, valor);
    return true;
  } catch (e) {
    return false;   // cota cheia ou modo privado: o guia só esquece, não quebra
  }
}

/**
 * Serializa a semente para dentro de um `<script>`, com segurança.
 *
 * ⚠️⚠️ `JSON.stringify` SOZINHO NÃO BASTA, e isto foi pego por teste: ele não
 *    escapa `<`. Um valor contendo `</script>` FECHA o bloco de script, e o que
 *    vier depois vira HTML executável dentro do iframe. Escapar `<` e `>` como
 *    `\uXXXX` mantém o JSON válido (o JS interpreta o escape ao ler a string) e
 *    torna impossível terminar a tag por dentro do literal.
 *
 * ⚠️ O `&` entra junto por precaução: o conteúdo é interpretado como script, não
 *    como HTML, mas o custo de escapá-lo é zero e ele fecha o caso de o bloco um
 *    dia ser lido noutro contexto.
 *
 * ⚠️ E sim, o valor VEM do próprio guia (ele grava, o portal devolve na visita
 *    seguinte). Tratar o que volta como confiável só porque saiu daqui é a
 *    suposição errada — no meio do caminho ele passou por um storage que outra
 *    coisa pode ter tocado.
 */
function paraScript(valor) {
  return JSON.stringify(valor)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');
}

/**
 * O id do bloco injetado. Compartilhado com o backend, que o usa para remover a
 * injeção antes de gravar uma revisão nova do guia — ver `gps-ti-versao.js`.
 */
export const ID_SHIM = 'portal-shim';

/**
 * O `<script>` do shim, já com a semente embutida.
 */
export function montarShimStorage(semente) {
  const dados = paraScript(semente || {});
  // ⚠️ O `id` NÃO é decorativo: é por ele que o backend ARRANCA este bloco do
  //    HTML que o Sebastião salva (`gps-ti-versao.js`). Sem o id, cada gravação
  //    publicaria mais uma cópia do shim — e, junto, a SEMENTE de quem salvou.
  return `<script id="${ID_SHIM}">(function(){
  /* Portal: memória emprestada ao guia (origem opaca não tem localStorage). */
  try { window.localStorage.getItem("gpsti-aviso"); return; } catch (e) {}
  var mem = {};
  try { mem = ${dados}; } catch (e) { mem = {}; }
  var falso = {
    getItem: function (k) { return Object.prototype.hasOwnProperty.call(mem, k) ? mem[k] : null; },
    setItem: function (k, v) {
      mem[k] = String(v);
      try { parent.postMessage({ tipo: "gps-ti:lembrar", chave: String(k), valor: String(v) }, "*"); } catch (e) {}
    },
    removeItem: function (k) { delete mem[k]; },
    clear: function () { mem = {}; },
    key: function (i) { return Object.keys(mem)[i] || null; }
  };
  Object.defineProperty(falso, "length", { get: function () { return Object.keys(mem).length; } });
  try {
    Object.defineProperty(window, "localStorage", { value: falso, configurable: true });
  } catch (e) { /* não deu: o guia volta a esquecer, como antes. Falha aberta. */ }
})();${FECHA_SCRIPT}`;
}

/**
 * Devolve o HTML do guia com o shim injetado ANTES de qualquer script dele.
 *
 * ⚠️ A posição é o ponto: o shim precisa estar instalado antes de o guia ler o
 *    storage. Entra logo depois do `<head>`, que é o primeiro lugar seguro.
 * ⚠️ Sem `<head>` o HTML volta INTACTO, sem shim — nunca meio injetado. Um guia
 *    que esquece é muito melhor que um guia que não abre.
 */
export function injetarShimStorage(html, semente) {
  if (typeof html !== 'string' || !html) return html;
  const i = html.search(/<head[^>]*>/i);
  if (i < 0) return html;
  const fim = html.indexOf('>', i) + 1;
  return html.slice(0, fim) + montarShimStorage(semente) + html.slice(fim);
}
